import React, { ReactNode, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LeftSidebarMenu } from './LeftSidebarMenu';
import { TopNavMenu } from './TopNavMenu';
import { Breadcrumb } from './Breadcrumb';
import { useConsumption } from '../../contexts/ConsumptionContext';
import { useAuth } from '../../contexts/AuthContext';

// Path usado como placeholder quando o usuário não possui foto
const DEFAULT_AVATAR = '/images/default-avatar.png';

// Gradientes do header mobile por tipo de página (mesmos do style.css original)
const mobileHeaderGradients = {
  water: 'linear-gradient(to right, rgba(44,142,181,1) 0%, rgba(44,142,181,1) 54%, rgba(77,209,213,1) 100%)',
  gas: 'linear-gradient(45deg, #f67b4a 0%, #fab937 100%)',
  energy: 'linear-gradient(45deg, #49ac18 0%, #9dbe19 100%)',
};

// Ícone genérico de usuário exibido quando não há foto (ou a foto falha ao carregar)
function GenericUserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} block`}
      viewBox="0 0 24 24"
      fill="#99abb4"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="profile"
      role="img"
    >
      <circle cx="12" cy="9" r="3.2" />
      <path d="M6 19a6 6 0 0 1 12 0Z" />
    </svg>
  );
}

// Avatar do usuário: usa a foto quando disponível, senão o ícone genérico
function UserAvatar({ profileUrl, className }: { profileUrl?: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const hasPhoto = !!profileUrl && profileUrl !== DEFAULT_AVATAR && !failed;

  if (!hasPhoto) {
    return <GenericUserIcon className={className} />;
  }

  return (
    <img
      src={profileUrl}
      alt="profile"
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

// Formata a data como no original (moment.js format)
function formatDate() {
  const now = new Date();
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const day = String(now.getDate()).padStart(2, '0');
  const month = months[now.getMonth()];
  const year = now.getFullYear();
  return { day: `${day} ${month}`, year: String(year) };
}

interface MainLayoutProps {
  children: ReactNode;
  // Flags de menu (equivalente aos helpers Rails)
  isGasMenu?: boolean;
  isWaterMenu?: boolean;
  isEnergyMenu?: boolean;
  isHomeMenu?: boolean;
  isReportMenu?: boolean;
  isGoalMenu?: boolean;
  isAdjustMenu?: boolean;
  // Permissões de acesso por tipo de medidor
  canAccessWater?: boolean;
  canAccessGas?: boolean;
  canAccessEnergy?: boolean;
  // Dados do usuário
  userFullName?: string;
  profileUrl?: string;
  isApiUser?: boolean;
  // Unidades
  units?: Array<{
    customerId: string;
    block: string;
    name: string;
    meterTypes: string[];
  }>;
  currentResidenceId?: string;
  // URLs (equivalente aos helpers Rails)
  urlForHomeMenu?: string;
  urlForReportMenu?: string;
  urlForGoalMenu?: string;
  editUserRegistrationPath?: string;
  destroyUserSessionPath?: string;
  // Breadcrumb
  commercialServiceName?: string;
  residenceName?: string;
  breadCrumbSection?: string;
  beginningOfPeriod?: string;
  flashMessages?: ReactNode;
  // i18n
  locale?: string;
}

export function MainLayout({
  children,
  isGasMenu = false,
  isWaterMenu = false,
  isEnergyMenu = false,
  isHomeMenu = false,
  isReportMenu = false,
  isGoalMenu = false,
  isAdjustMenu = false,
  canAccessWater = false,
  canAccessGas = false,
  canAccessEnergy = false,
  userFullName = '',
  profileUrl = '/images/default-avatar.png',
  isApiUser = false,
  units = [],
  currentResidenceId = '',
  urlForHomeMenu = '',
  urlForReportMenu = '',
  urlForGoalMenu = '',
  editUserRegistrationPath = '',
  destroyUserSessionPath: _destroyUserSessionPath = '/condomino/sair',
  commercialServiceName,
  residenceName,
  breadCrumbSection,
  beginningOfPeriod,
  flashMessages,
  locale: _locale = 'pt_BR',
}: MainLayoutProps) {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem('i18nextLng', lng);
    } catch {
      // ignora indisponibilidade de localStorage
    }
  };
  const navigate = useNavigate();
  const { setCurrentResidenceId } = useConsumption();
  const { logout } = useAuth();
  const { day, year } = formatDate();

  // Estado para controlar o select-imovel (collapsed/expanded)
  const [isSelectExpanded, setIsSelectExpanded] = useState(false);
  // Estado para controlar o dropdown de logout
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Estado do drawer lateral no mobile (substitui o toggle de classe no body)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();

  // Fecha o drawer ao navegar
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  // Fecha o drawer com Escape
  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDrawerOpen]);

  // Encontra a unidade atual para mostrar no .shown
  const currentUnit = units.find(u => u.customerId === currentResidenceId);
  const currentUnitText = currentUnit ? `${currentUnit.block} - ${currentUnit.name}` : '';

  // Calcula a altura quando expandido (40px por opção + 40px do shown)
  const expandedHeight = (units.length * 40) + 40;

  // Handler para toggle do select-imovel
  const handleSelectImovelToggle = () => {
    setIsSelectExpanded(!isSelectExpanded);
  };

  // Handler para selecionar uma opção
  const handleOptionClick = (unit: { customerId: string; meterTypes: string[] }, e: React.MouseEvent) => {
    e.stopPropagation(); // Não propagar para o container
    setIsSelectExpanded(false); // Fecha o dropdown
    setCurrentResidenceId(unit.customerId); // Atualiza o contexto global
    navigate(getUnitUrl(unit)); // Usa React Router para navegação suave
  };

  const getUnitUrl = (unit: { customerId: string; meterTypes: string[] }): string => {
    if (unit.meterTypes.includes('AGUA-FRIA') || unit.meterTypes.includes('AGUA-QUENTE')) {
      return `/consumo/agua/${unit.customerId}`;
    }
    if (unit.meterTypes.includes('GAS')) {
      return `/consumo/gas/${unit.customerId}`;
    }
    return `/consumo/energia/${unit.customerId}/consumo`;
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Determina a classe de página baseado no tipo de utility selecionado
  const pageClass = isGasMenu ? 'page-gas' : isEnergyMenu ? 'page-energy' : 'page-water';
  const pageType: 'water' | 'gas' | 'energy' = isGasMenu ? 'gas' : isEnergyMenu ? 'energy' : 'water';

  // Persiste o medidor selecionado para telas sem medidor na URL (ex.: perfil)
  // manterem o mesmo contexto/tema. Só grava em páginas de medidor.
  useEffect(() => {
    if (isGasMenu) localStorage.setItem('selected_meter', 'gas');
    else if (isEnergyMenu) localStorage.setItem('selected_meter', 'energia');
    else if (isWaterMenu) localStorage.setItem('selected_meter', 'agua');
  }, [isGasMenu, isEnergyMenu, isWaterMenu]);

  return (
    <>
      {/* Main wrapper */}
      <div id="main-wrapper" className={`w-full ${pageClass}`}>
        {/* Header */}
        <div className="header fixed top-0 left-0 right-0 z-50 bg-white">
          {/* Desktop navbar - hidden on mobile (max-812px) */}
          <nav className="top-navbar flex items-center h-[97px] pr-[15px] pl-0 max-[812px]:hidden">
            {/* Logo */}
            <div className="w-[100px] shrink-0 h-[97px] bg-white text-center shadow-[1px_0px_6px_2px_#eee]">
              <a
                className="block h-[97px] pt-[15px] mr-[9px] max-[812px]:hidden"
                href={urlForHomeMenu || '/'}
                onClick={(e) => { e.preventDefault(); navigate(urlForHomeMenu || '/'); }}
              >
                <img
                  src="/images/logo_topo_desktop.png"
                  alt="homepage"
                  className="dark-logo max-h-[61px] w-auto object-contain align-middle mx-auto"
                />
              </a>
              <a
                className="hidden min-[813px]:hidden"
                href={urlForHomeMenu || '/'}
                onClick={(e) => { e.preventDefault(); navigate(urlForHomeMenu || '/'); }}
              >
                <img
                  src="/images/logo_topo_mobile.png"
                  alt="homepage"
                  className="dark-logo"
                />
              </a>
            </div>
            {/* End Logo */}

            {/* Navbar collapse */}
            <div className="flex items-center flex-1">
              {/* Box date */}
              <div className="box-date border-r border-[#eee] h-[98px] py-[32px] px-[25px]">
                <span className="day block text-[#2988b5] font-medium text-[14px]">{day}</span>
                <span className="year block text-[#b1b1b1] font-light text-[14px]">{year}</span>
              </div>

              {/* Nav top - Desktop navigation */}
              <ul className="flex flex-row mr-auto h-[97px] max-[812px]:hidden list-none">
                <TopNavMenu
                  canAccessWater={canAccessWater}
                  canAccessGas={canAccessGas}
                  canAccessEnergy={canAccessEnergy}
                  isWaterMenu={isWaterMenu}
                  isGasMenu={isGasMenu}
                  isEnergyMenu={isEnergyMenu}
                  residenceId={currentResidenceId}
                />
              </ul>

              {/* Select imóvel - Property dropdown */}
              <div
                className="w-[16.666667%] max-w-full max-[812px]:hidden max-[838px]:flex-[0_0_23%] max-[838px]:max-w-full"
                id="select-imovel"
              >
                <div className="relative">
                  <div
                    className={`select-imovel absolute top-[-20px] w-full rounded-[10px] overflow-hidden text-[#ababab] font-light border-2 border-[#f0f0f0] cursor-pointer
                      before:content-[''] before:absolute before:bg-[#aeaeae] before:w-[10px] before:h-[10px] before:rotate-45 before:right-[15px]
                      after:content-[''] after:absolute after:bg-[#f8f8f8] after:w-[10px] after:h-[10px] after:rotate-45 after:right-[15px]
                      ${isSelectExpanded
                        ? 'before:top-[15px] after:top-[20px]'
                        : 'h-[40px] before:top-[10px] after:top-[5px]'
                      }`}
                    onClick={handleSelectImovelToggle}
                    style={{ height: isSelectExpanded ? `${expandedHeight}px` : '40px', transition: 'height 0.1s' }}
                  >
                    <div
                      className="shown w-full h-[40px] leading-[40px] pl-[15px] pr-[30px] text-[14px] bg-[#f8f8f8] cursor-pointer transition-all duration-300 truncate"
                      title={currentUnitText}
                    >
                      {currentUnitText}
                    </div>
                    {!isApiUser && units.map((unit) => (
                      <div
                        key={unit.customerId}
                        onClick={(e) => handleOptionClick(unit, e)}
                        className={`option w-full h-[40px] leading-[40px] pl-[15px] pr-[30px] text-[14px] bg-[#f8f8f8] cursor-pointer transition-all duration-300 hover:bg-white truncate
                          ${currentResidenceId === unit.customerId ? 'current' : ''}`}
                        title={`${unit.block} - ${unit.name}`}
                      >
                        {unit.block} - {unit.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right navbar items */}
              <ul className="flex flex-row list-none">
                {/* Money icon */}
                <li className="list-none border-l border-r border-[#eee] py-[24px] px-[10px] max-[812px]:hidden">
                  <a className="text-[#99abb4] block py-2" href={`${urlForReportMenu}?from_money_icon`}>
                    <i className="ico block w-[31px] h-[36px] bg-[url('/images/icons-set.png')] bg-no-repeat bg-[-81px_-58px]"></i>
                  </a>
                </li>

                {/* Notifications icon */}
                <li className="list-none py-[24px] px-[10px] max-[812px]:hidden">
                  <a className="text-[#99abb4] block py-2" href={`${urlForHomeMenu}#msg_status_goal_id`}>
                    <i className="ico block w-[31px] h-[36px] bg-[url('/images/icons-set.png')] bg-no-repeat bg-[-111px_-60px]"></i>
                  </a>
                </li>

                {/* Profile */}
                <li className="list-none">
                  <a 
                    className="text-[#99abb4] float-left pt-0 pb-0" 
                    href={editUserRegistrationPath}
                  >
                    <UserAvatar
                      profileUrl={profileUrl}
                      className="w-[98px] h-[98px]"
                    />
                  </a>
                  <div className="user-info w-[158px] py-[30px] pl-3 overflow-hidden max-[1008px]:hidden">
                    <span className="username block text-[#2988b5] font-bold">{userFullName}</span>
                    <span className="block text-[12px] text-[#bfbfbf]">{t('views.application.greetings')}</span>
                  </div>
                </li>

                {/* Logout dropdown */}
                <li className="list-none py-[30px] pl-3 pr-0 relative">
                  <a 
                    className="float-left cursor-pointer" 
                    href="#" 
                    onClick={handleDropdownToggle}
                    aria-haspopup="true" 
                    aria-expanded={isDropdownOpen}
                  >
                    <span className="fa fa-circle block text-[7px] text-[#cccdce]"></span>
                    <span className="fa fa-circle block text-[7px] text-[#cccdce]"></span>
                    <span className="fa fa-circle block text-[7px] text-[#cccdce]"></span>
                  </a>
                  {isDropdownOpen && (
                    <div className="!block absolute right-0 top-full min-w-[10rem] py-[0.5rem] bg-white border border-[rgba(120,130,140,0.13)] rounded shadow-[0_3px_12px_rgba(0,0,0,0.05)] z-[1000]">
                      <ul className="list-none p-0 m-0">
                        <li>
                          <a 
                            href="#" 
                            onClick={(e) => { e.preventDefault(); logout(); }}
                            className="block px-[15px] py-[9px] text-[#67757c] hover:bg-[#f2f4f8] hover:text-[#1976d2] hover:no-underline"
                          >
                            <i className="fa fa-power-off"></i>
                            &nbsp;&nbsp; {t('views.application.exit')}
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </li>
              </ul>
            </div>
          </nav>

          {/* Mobile navbar - visible only on mobile (max-812px) */}
          <nav className="hidden max-[812px]:block bg-white">
            {/* Linha 1: gradiente com hambúrguer | logo | data | avatar | sair (igual ao original) */}
            <div
              className="flex items-center h-[60px] pr-[8px]"
              style={{ background: mobileHeaderGradients[pageType] }}
            >
              <button
                type="button"
                onClick={() => setIsDrawerOpen(true)}
                aria-label={t('views.layouts.menu.open_menu')}
                className="w-[48px] h-[60px] shrink-0 flex items-center justify-center bg-transparent border-0 cursor-pointer text-white"
              >
                <i className="mdi mdi-menu text-[26px]"></i>
              </button>

              <a
                className="block shrink-0"
                href={urlForHomeMenu || '/'}
                onClick={(e) => { e.preventDefault(); navigate(urlForHomeMenu || '/'); }}
              >
                <img
                  src="/images/logo_topo_mobile.png"
                  alt="homepage"
                  className="dark-logo h-[32px] w-auto object-contain"
                />
              </a>

              <div className="ml-auto flex items-center min-w-0">
                {/* Data */}
                <div className="text-right leading-[15px] px-[8px] shrink-0">
                  <span className="block text-white text-[12px] font-medium">{day}</span>
                  <span className="block text-white text-[12px] font-light">{year}</span>
                </div>

                {/* Avatar -> perfil */}
                <a
                  href={editUserRegistrationPath}
                  className="shrink-0 flex items-center justify-center px-[4px]"
                >
                  <UserAvatar profileUrl={profileUrl} className="w-[44px] h-[44px] rounded-full object-cover" />
                </a>

                {/* Sair (3 pontos) */}
                <div className="relative shrink-0">
                  <a
                    className="flex flex-col items-center justify-center gap-[3px] w-[36px] h-[60px] cursor-pointer"
                    href="#"
                    onClick={handleDropdownToggle}
                    aria-haspopup="true"
                    aria-expanded={isDropdownOpen}
                  >
                    <span className="fa fa-circle block text-[7px] text-white"></span>
                    <span className="fa fa-circle block text-[7px] text-white"></span>
                    <span className="fa fa-circle block text-[7px] text-white"></span>
                  </a>
                  {isDropdownOpen && (
                    <div className="!block absolute right-0 top-full min-w-[10rem] py-[0.5rem] bg-white border border-[rgba(120,130,140,0.13)] rounded shadow-dropdown z-[1000]">
                      <ul className="list-none p-0 m-0">
                        <li>
                          <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); logout(); }}
                            className="block px-[15px] py-[9px] text-[#67757c] hover:bg-[#f2f4f8] hover:text-[#1976d2] hover:no-underline"
                          >
                            <i className="fa fa-power-off"></i>
                            &nbsp;&nbsp; {t('views.application.exit')}
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Linha 2: abas água/gás/energia | select imóvel | ícones (igual ao original) */}
            <div className="flex items-stretch h-[70px] bg-white">
              <ul className="flex flex-row h-full list-none shrink-0">
                <TopNavMenu
                  canAccessWater={canAccessWater}
                  canAccessGas={canAccessGas}
                  canAccessEnergy={canAccessEnergy}
                  isWaterMenu={isWaterMenu}
                  isGasMenu={isGasMenu}
                  isEnergyMenu={isEnergyMenu}
                  residenceId={currentResidenceId}
                />
              </ul>

              {/* Select imóvel */}
              <div className="flex-1 min-w-0 border-l border-r border-[#eee] relative">
                <div
                  className={`select-imovel absolute top-[15px] inset-x-0 text-center rounded-none border-0
                    before:w-[5px] before:h-[5px] before:right-0 before:left-0 before:mx-auto
                    after:w-[5px] after:h-[5px] after:right-0 after:left-0 after:mx-auto
                    ${isSelectExpanded
                      ? 'before:top-[35px] after:top-[37px]'
                      : 'h-[40px] before:top-[33px] after:top-[31px]'
                    }`}
                  onClick={handleSelectImovelToggle}
                  style={{ height: isSelectExpanded ? `${expandedHeight}px` : '40px', transition: 'height 0.1s' }}
                >
                  <div
                    className="shown text-[11px] px-[10px] bg-white leading-[39px] h-[39px] truncate"
                    title={currentUnitText}
                  >
                    {currentUnitText}
                  </div>
                  {!isApiUser && units.map((unit) => (
                    <div
                      key={unit.customerId}
                      onClick={(e) => handleOptionClick(unit, e)}
                      className={`option text-[11px] px-[10px] bg-white leading-[39px] h-[39px] truncate
                        ${currentResidenceId === unit.customerId ? 'current' : ''}`}
                      title={`${unit.block} - ${unit.name}`}
                    >
                      {unit.block} - {unit.name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ícones extrato e notificações */}
              <ul className="flex flex-row items-center list-none shrink-0">
                <li className="list-none h-full">
                  <a
                    className="flex items-center justify-center w-[52px] h-full text-[#99abb4]"
                    href={`${urlForReportMenu}?from_money_icon`}
                  >
                    <i className="ico block w-[31px] h-[36px] bg-[url('/images/icons-set.png')] bg-no-repeat bg-[-81px_-58px]"></i>
                  </a>
                </li>
                <li className="list-none h-full border-l border-[#eee]">
                  <a
                    className="flex items-center justify-center w-[52px] h-full text-[#99abb4]"
                    href={`${urlForHomeMenu}#msg_status_goal_id`}
                  >
                    <i className="ico block w-[31px] h-[36px] bg-[url('/images/icons-set.png')] bg-no-repeat bg-[-111px_-60px]"></i>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>
        {/* End Header */}

        {/* Backdrop do drawer mobile */}
        {isDrawerOpen && (
          <div
            className="fixed inset-0 z-[55] bg-black/40 min-[813px]:hidden"
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Left Sidebar */}
        <LeftSidebarMenu
          isHomeMenu={isHomeMenu}
          isReportMenu={isReportMenu}
          isGoalMenu={isGoalMenu}
          isAdjustMenu={isAdjustMenu}
          urlForHomeMenu={urlForHomeMenu}
          urlForReportMenu={urlForReportMenu}
          urlForGoalMenu={urlForGoalMenu}
          editUserRegistrationPath={editUserRegistrationPath}
          isApiUser={isApiUser}
          pageType={pageType}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onLogout={logout}
        />
        {/* End Left Sidebar */}

        {/* Page wrapper */}
        <div className="page-wrapper bg-[#f0f0f0] pb-[60px] min-h-screen ml-[100px] mt-[97px] max-[812px]:ml-0 max-[812px]:mt-[130px]">
          {/* Breadcrumb */}
          {!isApiUser && (
            <Breadcrumb
              flashMessages={flashMessages}
              commercialServiceName={commercialServiceName}
              residenceName={residenceName}
              residenceId={currentResidenceId}
              urlForHomeMenu={urlForHomeMenu}
              breadCrumbSection={breadCrumbSection}
              beginningOfPeriod={beginningOfPeriod}
              pageType={pageType}
            />
          )}
          {/* End Breadcrumb */}

          {/* Container fluid */}
          <div className="w-full px-4 md:px-8 lg:px-[60px] pb-[25px]">
            {/* Page Content */}
            <div className="flex flex-wrap">
              {children}
            </div>
            {/* End Page Content */}
          </div>
          {/* End Container fluid */}
        </div>

        {/* Footer */}
        <div id="footer">
          <div className="container mx-auto px-[15px]">
            <p className="text-right m-[4px] w-full text-[12px]">
              <button type="button" onClick={() => changeLanguage('en')} className="text-[#4680ff] hover:no-underline bg-transparent border-0 p-0 cursor-pointer">English</button> |
              <button type="button" onClick={() => changeLanguage('es')} className="text-[#4680ff] hover:no-underline bg-transparent border-0 p-0 cursor-pointer">Español</button> |
              <button type="button" onClick={() => changeLanguage('pt')} className="text-[#4680ff] hover:no-underline bg-transparent border-0 p-0 cursor-pointer">Português</button>
            </p>
          </div>
        </div>
        {/* End Footer */}
      </div>
      {/* End Wrapper */}
    </>
  );
}
